import type { Reservation, ReservationPartyDraft } from '../Types/reservationTypes';
import type { ReservationPartyLookup } from '../Service/reservationPartiesLookupMapper';

export interface ReservationApplicantDisplay {
  title: string;
  subtitle: string;
}

const NEW_PARTY_SENTINEL = '__new__';
type WidenProperty<T, K extends keyof T, V> = {
  [P in keyof T]: P extends K ? T[P] | V : T[P];
};
type ReservationApplicantDisplaySource = WidenProperty<Reservation, 'applicantPartyId', string>;

const normalizePartyId = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) {
    return value;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = value.trim();

  if (!normalizedValue || normalizedValue === NEW_PARTY_SENTINEL) {
    return undefined;
  }

  if (!/^\d+$/.test(normalizedValue)) {
    return undefined;
  }

  const parsedValue = Number(normalizedValue);

  if (!Number.isSafeInteger(parsedValue) || parsedValue <= 0) {
    return undefined;
  }

  return parsedValue;
};

const joinNonEmpty = (parts: Array<string | undefined | null>, separator: string): string =>
  parts
    .map((part) => part?.trim() ?? '')
    .filter(Boolean)
    .join(separator);

const getPartyDraftLabel = (draft: ReservationPartyDraft | undefined): string => {
  if (!draft) {
    return '';
  }

  if (draft.partyType === 'organization') {
    return draft.organizationName.trim();
  }

  return joinNonEmpty([draft.firstName, draft.lastName], ' ');
};

const getPartyDraftAddress = (draft: ReservationPartyDraft | undefined): string => {
  if (!draft) {
    return '';
  }

  return joinNonEmpty(
    [draft.street, joinNonEmpty([draft.postalCode, draft.city], ' '), draft.countryCode],
    ', '
  );
};

export const getReservationApplicantDisplay = (
  reservation: ReservationApplicantDisplaySource,
  parties: ReservationPartyLookup[] = []
): ReservationApplicantDisplay => {
  const partyId = normalizePartyId(reservation.applicantPartyId);
  const party = partyId !== undefined ? parties.find((item) => item.id === partyId) : undefined;

  if (party) {
    const partyLabel = party.label?.trim();
    const partyAddressSummary = party.addressSummary?.trim();

    return {
      title: partyLabel || `Party #${party.id}`,
      subtitle: partyAddressSummary || 'Brak adresu',
    };
  }

  const draftLabel = getPartyDraftLabel(reservation.applicantPartyDraft);
  const draftAddress = getPartyDraftAddress(reservation.applicantPartyDraft);
  const isNewPartyDraftFlow =
    typeof reservation.applicantPartyId === 'string' &&
    reservation.applicantPartyId.trim() === NEW_PARTY_SENTINEL;

  if (isNewPartyDraftFlow) {
    return {
      title: draftLabel || 'Nowa strona',
      subtitle: draftAddress || 'Nowa strona bez adresu',
    };
  }

  if (partyId !== undefined) {
    return {
      title: `Party #${partyId}`,
      subtitle: 'Brak danych strony w lookup',
    };
  }

  return {
    title: 'Brak strony zgłaszającej',
    subtitle: 'Brak danych strony zgłaszającej',
  };
};
